/**
 * Whose bank account a concentration account is.
 *
 * Each fund series is its own legal entity with its own bank account. A client of a
 * product is shown the account of the fund that receives that product's capital, so
 * an account that belongs to no fund is shown to nobody, and a receiving fund with no
 * account is a product clients cannot fund.
 */
import type { AnnouncedEntity, ConcentrationAccount, ReceivingEntity } from '../../types/finance';

/** What the list says under "Fund" for one account. */
export function fundLabel(a: Pick<ConcentrationAccount, 'legal_entity_id' | 'receives_for'>): string {
  if (a.legal_entity_id === null) return 'Not assigned to a fund — shown to no client';
  if (a.receives_for.length === 0) return 'Its fund is not receiving new capital right now';
  return `Receives ${a.receives_for.join(', ')}`;
}

/** Funds that receive client money and have no account registered, most products first. */
export function fundsWithoutAccount(entities: ReceivingEntity[]): ReceivingEntity[] {
  return entities
    .filter((e) => !e.has_account)
    .sort((a, b) => b.products.length - a.products.length || a.products[0].localeCompare(b.products[0]));
}

/** The places of a Mexican issuer, as the lines staff read (nothing for an issuer with no slots). */
export function holderPlaceRows(e: Pick<ReceivingEntity, 'holder_slots'>): Array<[string, string]> {
  const s = e.holder_slots;
  if (!s) return [];
  const rows: Array<[string, string]> = [
    ['Counted, with a balance', String(s.counted_with_balance)],
    ['Counted at zero, within the grace', String(s.counted_at_zero)],
  ];
  if (s.grace_ends_on.length > 0) {
    rows.push(['Grace ends on', s.grace_ends_on.join(', ')]);
  }
  rows.push(
    ['Reserved by a signature', String(s.reserved)],
    ['Former clients released', String(s.former_clients)],
    ['Room left', `${s.free} of ${s.limit}`],
  );
  return rows;
}

/** One line a super_admin reads to pick the fund an account is for. */
export function entityOption(e: Pick<ReceivingEntity, 'products' | 'currency'>): string {
  return `${e.products.join(', ')} · ${e.currency}`;
}

/** A company books announced, as one choice of the picker: its id and the line that names it. */
export interface AnnouncedChoice {
  legal_entity_id: string;
  label: string;
  currency: string | null;
}

/**
 * The companies books announced that a fund not receiving yet is picked from: those not already
 * offered as a receiving fund, each named by its products, country and currency. `countryName`
 * turns a stored code into the name the reader reads. Two companies that would read the same are
 * told apart by the start of their id.
 */
export function announcedChoices(
  announced: AnnouncedEntity[],
  offeredAbove: ReadonlyArray<Pick<ReceivingEntity, 'legal_entity_id'>>,
  countryName: (code: string) => string,
): AnnouncedChoice[] {
  const above = new Set(offeredAbove.map((e) => e.legal_entity_id));
  const choices = announced
    .filter((e) => !above.has(e.legal_entity_id))
    .map((e) => ({
      legal_entity_id: e.legal_entity_id,
      currency: e.currency,
      label: [
        e.name ?? `Company without a product, id ${e.legal_entity_id.slice(0, 8)}`,
        e.country ? countryName(e.country) || e.country : null,
        e.currency,
      ]
        .filter(Boolean)
        .join(' · '),
    }));
  const count = new Map<string, number>();
  for (const c of choices) count.set(c.label, (count.get(c.label) ?? 0) + 1);
  return choices.map((c) =>
    (count.get(c.label) ?? 0) > 1
      ? { ...c, label: `${c.label} · id ${c.legal_entity_id.slice(0, 8)}` }
      : c,
  );
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Why the account cannot be created yet, or null when it can. The fund is required:
 *  the server refuses an account of no fund. */
export function fundProblem(legalEntityId: string): string | null {
  const value = legalEntityId.trim();
  if (value === '') return 'Choose the fund this account belongs to.';
  if (!UUID.test(value)) return 'The fund’s legal entity id is not valid. Copy it from the funds console.';
  return null;
}

/** Where an account stands. Only one in force is shown to clients and credited. */
export type AccountStanding = 'waiting' | 'in_force' | 'retired';

export function accountStanding(
  a: Pick<ConcentrationAccount, 'confirmed_at' | 'is_active' | 'effective_to'>,
): AccountStanding {
  if (!a.is_active || a.effective_to !== null) return 'retired';
  return a.confirmed_at === null ? 'waiting' : 'in_force';
}

export const STANDING_LABEL: Record<AccountStanding, string> = {
  waiting: 'Waiting for a second super admin',
  in_force: 'In force',
  retired: 'Retired',
};

/** Putting an account in force takes a second person: never the one who entered it. */
export function mayConfirm(
  a: Pick<ConcentrationAccount, 'confirmed_at' | 'is_active' | 'effective_to' | 'entered_by_me'>,
): boolean {
  return accountStanding(a) === 'waiting' && !a.entered_by_me;
}

/** What the row says while it waits, to the person reading it. */
export function waitingNote(a: Pick<ConcentrationAccount, 'entered_by_me'>): string {
  return a.entered_by_me
    ? 'You entered or changed it, so a different super admin has to confirm it. Clients do not see it yet.'
    : 'Not in force yet. Check the details against the bank’s own document before you confirm.';
}

/** The details of an account in force never change; its replacement is entered and confirmed. */
export function detailsFrozen(a: Pick<ConcentrationAccount, 'confirmed_at'> | null): boolean {
  return a !== null && a.confirmed_at !== null;
}
